/* File: userInfoModal.jsx
 * Path: ananunadm/src/components/main/userInfoModal.jsx
*/
import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import config from "@/config";
import { useUser } from "@/helpers/userContext";
import {
	Flex,
	Text,
	Modal,
	Card,
	Grid,
	Avatar,
	Button,
	Badge,
	Select,
	Group,
	Timeline,
	Drawer,
	Divider,
	ScrollArea
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useSocket } from "@/helpers/socket";
import { T } from "@/helpers/translator";
import { IconLogin, IconRefresh, IconTrash, IconArrowRight, IconBuilding, IconDoorExit, IconViewfinder, IconDeviceFloppy, IconX, IconDownload, IconEye, IconPlus, IconUpload, IconMail } from "@tabler/icons-react";
import moment from "moment";
import { IconLogout, IconSettings } from "@tabler/icons-react";
import classes from "./UserInfoIcons.module.css";
import { useAuth } from "@/helpers/auth";
import { useLang, LanguagePicker } from "@/helpers/language";
import { getUserImageSrc } from "@/helpers/uploadImage";
import * as API from "@/helpers/api";
import GoogleSyncMenu from "./GoogleSyncMenu";
import { ACTIVITY_HISTORY_EVENT, ACTIVITY_STATE_EVENT } from "./activityTracker";

/** Set true to show room select + check-in/out in the user drawer. */
const SHOW_OFFICE_PRESENCE = false;
const SUPPORT_EMAIL = "info@deepmindsystems.com";

const EVENT_STYLES = {
	entered: { icon: IconLogin, color: "#0ca678" },
	login: { icon: IconLogin, color: "#0ca678" },
	view: { icon: IconEye, color: "#228be6" },
	viewed: { icon: IconEye, color: "#228be6" },
	created: { icon: IconPlus, color: "#12b886" },
	create: { icon: IconPlus, color: "#12b886" },
	updated: { icon: IconRefresh, color: "#15aabf" },
	update: { icon: IconRefresh, color: "#15aabf" },
	saved: { icon: IconDeviceFloppy, color: "#40c057" },
	save: { icon: IconDeviceFloppy, color: "#40c057" },
	cancelled: { icon: IconX, color: "#f59f00" },
	canceled: { icon: IconX, color: "#f59f00" },
	cancel: { icon: IconX, color: "#f59f00" },
	downloaded: { icon: IconDownload, color: "#7950f2" },
	download: { icon: IconDownload, color: "#7950f2" },
	uploaded: { icon: IconUpload, color: "#7048e8" },
	upload: { icon: IconUpload, color: "#7048e8" },
	deleted: { icon: IconTrash, color: "#fa5252" },
	delete: { icon: IconTrash, color: "#fa5252" },
	logout: { icon: IconLogout, color: "#e64980" },
	exited: { icon: IconLogout, color: "#e64980" }
};

const getEventStyle = (item, lang) => {
	const method = String(item?.action?.method || "").trim().toLowerCase();
	const name = String(item?.action?.name?.[lang] || item?.action?.name?.en || "").trim().toLowerCase();
	const matchedKey = Object.keys(EVENT_STYLES).find((key) => method === key || name.includes(key));
	return EVENT_STYLES[matchedKey] || { icon: IconArrowRight, color: "#868e96" };
};

function UserInfoModal({ isMobile, compact }) {
	const { logout } = useAuth();
	const { user } = useUser();
	const { lang } = useLang();
	const { socket } = useSocket();
	const [{ activeTime = 0, inactiveTime = 0 }, setTimeLapse] = useState(() => {
		const today = moment().format("YYYY-MM-DD");
		const todayRecord = user?.timeLapse?.find(
			(item) => moment(item?.date).format("YYYY-MM-DD") === today
		);
		return todayRecord || { activeTime: 0, inactiveTime: 0 };
	});
	const [opened, OpenClose] = useDisclosure(false);
	const [activityStatus, setActivityStatus] = useState("active");
	const [activityHistory, setActivityHistory] = useState(user?.history || []);
	const history = useHistory();

	const { connectedUsers } = useSocket();
	const [inOffice, setInOffice] = useState(false);
	const [selectedRoomId, setSelectedRoomId] = useState(null);
	const [officeRooms, setOfficeRooms] = useState([]);

	useEffect(() => {
		const updateActivity = (event) => {
			setTimeLapse({
				activeTime: event.detail?.activeTime || 0,
				inactiveTime: event.detail?.inactiveTime || 0
			});
			setActivityStatus(event.detail?.status || "active");
		};
		window.addEventListener(ACTIVITY_STATE_EVENT, updateActivity);
		return () => window.removeEventListener(ACTIVITY_STATE_EVENT, updateActivity);
	}, []);

	useEffect(() => setActivityHistory(user?.history || []), [user?.history]);

	useEffect(() => {
		const updateHistory = (event) => setActivityHistory(event.detail?.history || []);
		window.addEventListener(ACTIVITY_HISTORY_EVENT, updateHistory);
		return () => window.removeEventListener(ACTIVITY_HISTORY_EVENT, updateHistory);
	}, []);

	useEffect(() => {
		const me = connectedUsers.find((u) => u._id === user?._id);
		setInOffice(me?.inOffice || false);
		if (!me?.inOffice) setSelectedRoomId(null);
	}, [connectedUsers, user?._id]);

	useEffect(() => {
		if (!SHOW_OFFICE_PRESENCE || !opened || officeRooms.length > 0) return;
		API.get({ route: config.api.officeMap.room, populate: ["room"] })
			.then((rooms) => {
				if (Array.isArray(rooms)) {
					setOfficeRooms(rooms.map((r) => ({
						value: r._id,
						label: r.room?.name || r.name || r._id,
					})));
				}
			})
			.catch(() => {});
	}, [opened]);

	const handleCheckIn = () => {
		if (!socket || !user?._id) return;
		socket.emit("userCheckIn", { userId: user._id, roomId: selectedRoomId || null });
	};

	const handleCheckOut = () => {
		if (!socket || !user?._id) return;
		socket.emit("userCheckOut", user._id);
	};

	const userAvatarSrc = getUserImageSrc(user, { targetWidth: 94 });

	return (
		<>
			<Avatar
				data-testid="user-menu-avatar"
				ml={compact ? 0 : isMobile ? 5 : 10}
				style={{
					cursor: "pointer",
					alignSelf: "right",
					border: "1px solid rgba(128,128,128,0.35)",
					width: 40,
					height: 40,
					minWidth: 40,
				}}
				onClick={OpenClose.toggle}
				src={userAvatarSrc}
				size={40}
				radius="sm"
				alt="user-avatar"
			/>
			<Drawer
				opened={opened}
				onClose={OpenClose.close}
				position="right"
				title={""}
				overlayProps={{
					opacity: 0.55,
					blur: 3
				}}
			>
				<Grid>
					<Grid.Col span={5}>
						<Avatar
							src={userAvatarSrc}
							size={94}
							radius="md"
						/>
					</Grid.Col>
					<Grid.Col span={7}>
						<Flex wrap="nowrap" mt={1} direction={"column"} gap={10}>
							<div>
								<Text size="lg" weight={500} className={classes.name}>
									{user.name?.[lang]}
								</Text>
								<Text size="xs" transform="uppercase" weight={700} color="dimmed">
									{user.position?.name?.[lang]}
								</Text>
								{user.username && (
									<Text size="xs" color="dimmed">
										@{user.username}
									</Text>
								)}
							</div>
							{config.hasPagePermission(user, "userDashboard") && (
								<Button
									justify="center"
									onClick={() => {
										if (!user?._id) return;
										OpenClose.close();
										history.push(`/hr/user-dashboard/${user._id}`);
									}}
									p={6}
									size="sm"
									leftSection={
										<IconViewfinder
											size="1rem"
											className={classes.icon}
										/>
									}
									variant="filled"
								>
									{T("userDashboard", lang)}
								</Button>
							)}
							<GoogleSyncMenu isMobile={isMobile} trigger="button" />
						</Flex>
					</Grid.Col>
				</Grid>

				<Divider my="md" />

				<Flex mb={10} direction="column" gap={4}>
					<Flex justify="space-between" align="center">
						<Text size="sm" transform="uppercase" weight={700} color="dimmed">Status</Text>
						<Badge color={activityStatus === "active" ? "green" : "orange"} variant="light">
							{activityStatus === "active" ? "Active" : "Passive"}
						</Badge>
					</Flex>
					<Flex justify="space-between">
						<Text size="sm" transform="uppercase" weight={700} color="dimmed">
							{T("lastLogin", lang)}
						</Text>
						<Text size="sm" transform="uppercase" weight={700} color="dimmed">
							{moment(user.lastLogin).format("DD.MM.YYYY HH:mm")}
						</Text>
					</Flex>
					<Flex justify="space-between">
						<Text size="sm" transform="uppercase" weight={700} color="dimmed">
							{T("activeTime", lang)}
						</Text>
						<Text size="sm" transform="uppercase" weight={700} color="dimmed">
							{activeTime ? moment.utc(activeTime * 1000).format("HH:mm:ss") : "00:00:00"}
						</Text>
					</Flex>
					<Flex justify="space-between">
						<Text size="sm" transform="uppercase" weight={700} color="dimmed">
							{T("inactiveTime", lang)}
						</Text>
						<Text size="sm" transform="uppercase" weight={700} color="dimmed">
							{inactiveTime ? moment.utc(inactiveTime * 1000).format("HH:mm:ss") : "00:00:00"}
						</Text>
					</Flex>
				</Flex>

				{SHOW_OFFICE_PRESENCE && (
					<>
						<Divider my="md" />

						<Flex direction="column" gap={8}>
							<Group spacing={8}>
								<IconBuilding size={16} color="#228be6" />
								<Text size="sm" weight={600} color="dimmed" transform="uppercase">
									{T("officePresence", lang) || "Office Presence"}
								</Text>
								{inOffice && (
									<Badge color="blue" variant="filled" size="sm">
										{T("inOffice", lang) || "In Office"}
									</Badge>
								)}
							</Group>
							{!inOffice && (
								<Select
									placeholder={T("selectRoom", lang) || "Select room (optional)"}
									data={officeRooms}
									value={selectedRoomId}
									onChange={setSelectedRoomId}
									clearable
									size="xs"
								/>
							)}
							<Button
								size="xs"
								color={inOffice ? "gray" : "blue"}
								variant={inOffice ? "outline" : "filled"}
								leftSection={inOffice ? <IconDoorExit size={14} /> : <IconBuilding size={14} />}
								onClick={inOffice ? handleCheckOut : handleCheckIn}
							>
								{inOffice ? (T("checkOut", lang) || "Check Out") : (T("checkIn", lang) || "Check In")}
							</Button>
						</Flex>
					</>
				)}

				<Divider my="md" />

				<Text size="lg">
					{T("history", lang)}
				</Text>

				<ScrollArea
					style={{ height: "calc(100vh - 490px)" }}
					scrollbarSize={8}
					mt={10}
					type="auto"
					offsetScrollbars
				>
					{activityHistory.length > 0 && (
						<Card withBorder shadow="sm" radius="md">
							<Timeline bulletSize={28} lineWidth={2}>
								{activityHistory.map((item, index) => {
									const method = item?.action?.method;
									const eventStyle = getEventStyle(item, lang);
									const EventIcon = eventStyle.icon;
									const eventName = item?.action?.name?.[lang] || item?.action?.name?.en || method || "Activity";
									const metadata = item?.metadata || {};
									const labelPath = Array.isArray(metadata.labelPath) ? metadata.labelPath : [];
									return (
										<Timeline.Item
											bullet={
												<span style={{
													width: 28,
													height: 28,
													borderRadius: "50%",
													backgroundColor: eventStyle.color,
													display: "inline-flex",
													alignItems: "center",
													justifyContent: "center"
												}}>
													<EventIcon size={16} stroke={2.2} color="#ffffff" />
												</span>
											}
											title={`${moment(item?.date).format("HH:mm")} - ${eventName}`}
											key={index}
										>
											{labelPath.length > 0 && (
												<Text weight={600} size="sm">{labelPath.join(" > ")}</Text>
											)}
											{item?.action?.route.pathname && (
												<Flex
													style={{
														cursor: "pointer",
														flexWrap: "wrap",
														maxWidth: "100%",
														overflowX: "hidden"
													}}
													onClick={() => {
														OpenClose.close();
														history.push(
															"/" + item?.action?.route.pathname + "?" + item?.action?.route.search
														);
													}}
												>
													{labelPath.length === 0 && (
														<Text color="dimmed" size="sm" style={{ wordBreak: "break-word" }}>
															{item?.action?.route.pathname}{item?.action?.route.search ? `?${item.action.route.search}` : ""}
														</Text>
													)}
												</Flex>
											)}
											{Number(metadata.durationMs) > 0 && (
												<Text color="dimmed" size="xs">Stayed: {Math.round(metadata.durationMs / 1000)} sec</Text>
											)}
											{Array.isArray(metadata.previousLabelPath) && metadata.previousLabelPath.length > 0 && (
												<Text color="dimmed" size="xs">Previous: {metadata.previousLabelPath.join(" > ")}</Text>
											)}
										</Timeline.Item>
									);
								})}
							</Timeline>
						</Card>
					)}
				</ScrollArea>

				<Button
					component="a"
					href={`mailto:${SUPPORT_EMAIL}`}
					justify="center"
					mt={16}
					fullWidth
					size="sm"
					variant="light"
					leftSection={<IconMail size="1rem" className={classes.icon} />}
				>
					Write email to us
				</Button>

				<Button
					justify="center"
					mt={8}
					fullWidth
					onClick={() => logout(socket)}
					size="sm"
					color="red"
					variant="filled"
					leftSection={<IconLogout size="1rem" className={classes.icon} />}
				>
					{T("logOut", lang)}
				</Button>

				{/* <Card withBorder shadow="sm" radius="md" mt={20}>
					<Card.Section withBorder inheritPadding py="xs">
						{T("platformSpecificSettings", lang)}
					</Card.Section>
					<Card.Section withBorder inheritPadding py="xs">
						<Link to="/system/permission" onClick={OpenClose2.close}>
							<Text color="blue" size="md">
								{T("permissions", lang)}
							</Text>
						</Link>
					</Card.Section>

					<Card.Section withBorder inheritPadding py="xs">
						<Link to="/system/role" onClick={OpenClose2.close}>
							<Text color="blue" size="md">
								{T("roles", lang)}
							</Text>
						</Link>
					</Card.Section>
				</Card> */}
			</Drawer>
		</>
	);
}

export default UserInfoModal;
